import {
    eachDayOfInterval,
    eachHourOfInterval,
    eachMonthOfInterval,
    eachWeekOfInterval,
    endOfDay,
    endOfHour,
    endOfMinute,
    endOfMonth,
    endOfQuarter,
    endOfWeek,
    endOfYear,
    getDaysInMonth,
    getWeeksInMonth,
    startOfDay,
    startOfMonth,
    startOfQuarter,
    startOfWeek,
    startOfYear,
    utc,
} from '@blockworks/platform/libs/date-fns';

const startOfWeekOnMonday = (date: Date, options?: Parameters<typeof startOfWeek>[1]) =>
    startOfWeek(date, { weekStartsOn: 1, ...options });
const endOfWeekOnMonday = (date: Date, options?: Parameters<typeof endOfWeek>[1]) =>
    endOfWeek(date, { weekStartsOn: 1, ...options });

const endOfInterval = {
    minutes: endOfMinute,
    hours: endOfHour,
    days: endOfDay,
    weeks: endOfWeekOnMonday,
    months: endOfMonth,
    quarters: endOfQuarter,
    years: endOfYear,
};

const intervalBounds = {
    days: {
        end: endOfDay,
        start: startOfDay,
    },
    weeks: { end: endOfWeekOnMonday, start: startOfWeekOnMonday },
    months: { end: endOfMonth, start: startOfMonth },
    quarters: { end: endOfQuarter, start: startOfQuarter },
    years: { end: endOfYear, start: startOfYear },
};

const maxRowsPerInterval: Record<string, number> = {
    minutes: 60 * 6, // 6 Hours
    hours: 24 * 7 * 2, // 2 Weeks
    days: 90, // 3 Months
    weeks: 52 * 2, // 2 Years
    months: 12 * 5, // 5 Years
    quarters: Infinity, // all
    years: Infinity, // all
};

const getIntervalSize = {
    hours: {
        minutes: () => 60,
    },
    days: {
        minutes: () => 60 * 24,
        hours: () => 24,
    },
    weeks: {
        minutes: () => 60 * 24 * 7,
        hours: () => 24 * 7,
        days: () => 7,
    },
    months: {
        hours: (date: Date) =>
            eachHourOfInterval({ start: startOfMonth(date), end: endOfMonth(date) }, { in: utc }).length,
        days: (date: Date) => getDaysInMonth(date, { in: utc }),
        weeks: (date: Date) => getWeeksInMonth(date, { weekStartsOn: 1, in: utc }),
    },
    quarters: {
        hours: (date: Date) =>
            eachHourOfInterval({ start: startOfQuarter(date), end: endOfQuarter(date) }, { in: utc }).length,
        days: (date: Date) =>
            eachDayOfInterval({ start: startOfQuarter(date), end: endOfQuarter(date) }, { in: utc }).length,
        weeks: (date: Date) =>
            eachWeekOfInterval({ start: startOfQuarter(date), end: endOfQuarter(date) }, { in: utc }).length,
        months: (date: Date) =>
            eachMonthOfInterval({ start: startOfQuarter(date), end: endOfQuarter(date) }, { in: utc }).length,
    },
    years: {
        hours: () => 365 * 24,
        days: () => 365,
        weeks: () => 52,
        months: () => 12,
        quarters: () => 4,
    },
};

export { endOfInterval, getIntervalSize, intervalBounds, maxRowsPerInterval };
